import React, { lazy } from 'react';
import type { NavigateFunction } from 'react-router-dom';

import { SysAdminTooltip } from '@sine/sine-ui-components';

import { AdminFeatures } from '../../services/features/feature.constants';
import wrapLazyLoad from '../hocs/wrapLazyLoad';
import type { Page } from './pages.types';

const SettingsPage = wrapLazyLoad(lazy(() => import('../../pages/Settings/SettingsPage')));
const PluginsPage = wrapLazyLoad(lazy(() => import('../../pages/Plugins/PluginsRouter')));
const PeoplePage = wrapLazyLoad(lazy(() => import('../../pages/People/PeoplePage')));
const ReportsPage = wrapLazyLoad(lazy(() => import('../../pages/Reports/ReportsOverview')));
const FeaturesPage = wrapLazyLoad(lazy(() => import('../../pages/Features/FeaturesPage')));
const SamlSsoPage = wrapLazyLoad(lazy(() => import('../../pages/SamlSso/SamlSsoPage')));

export function getPages(
  pushPage: NavigateFunction,
  isImpersonating: boolean,
  isTeamAdmin: boolean,
  enabledFeatures: Array<string>,
  launchdarklyFlags: Record<string, boolean>,
): Array<Page> {
  return [
    {
      'path': '/people',
      'hasSubPages': false,
      'label': 'People',
      'icon': 'team',
      'hasPermission': isImpersonating || isTeamAdmin,
      'Component': PeoplePage,
      'onClick': () => pushPage('/people'),
      'data-st-ext': 'sidebar-menu-item-people',
    },
    {
      'path': '/plugins',
      'hasSubPages': true,
      'label': 'Plugins',
      'icon': 'plugins',
      'hasPermission': isImpersonating || (isTeamAdmin && enabledFeatures.includes(AdminFeatures.ADMIN_PLUGINS)),
      'Component': PluginsPage,
      'onClick': () => pushPage('/plugins'),
      'data-st-ext': 'sidebar-menu-item-plugins',
    },
    {
      'path': '/reports',
      'hasSubPages': true,
      'label': 'Reports',
      'icon': 'reports',
      'iconRight':
        isImpersonating && !enabledFeatures.includes(AdminFeatures.ADVANCED_REPORTING) ? (
          <SysAdminTooltip placement='right' />
        ) : undefined,
      'hasPermission': isImpersonating || (isTeamAdmin && launchdarklyFlags.adminClientAdvancedReporting),
      'Component': ReportsPage,
      'onClick': () => pushPage('/reports'),
      'data-st-ext': 'sidebar-menu-item-reports',
    },
    {
      'path': '/settings',
      'hasSubPages': true,
      'label': 'Settings',
      'icon': 'settings',
      'hasPermission': isImpersonating || isTeamAdmin,
      'Component': SettingsPage,
      'onClick': () => pushPage('/settings'),
      'data-st-ext': 'sidebar-menu-item-settings',
    },
    {
      'path': '/features',
      'hasSubPages': false,
      'label': 'Features',
      'icon': 'key',
      'iconRight': <SysAdminTooltip placement='right' />,
      'hasPermission': isImpersonating,
      'Component': FeaturesPage,
      'onClick': () => pushPage('/features'),
      'data-st-ext': 'sidebar-menu-item-features',
    },
    {
      'path': '/saml-sso',
      'hasSubPages': false,
      'label': 'SAML SSO',
      'icon': 'security',
      'iconRight': <SysAdminTooltip placement='right' />,
      'hasPermission': isImpersonating,
      'Component': SamlSsoPage,
      'onClick': () => pushPage('/saml-sso'),
      'data-st-ext': 'sidebar-menu-item-saml-sso',
    },
  ];
}
